
import { useState } from 'react';

interface EnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEnroll: (userData: {
    name: string;
    email: string;
    phone: string;
    institution: string;
    password: string;
  }) => void;
  courseName: string;
}

export default function EnrollmentModal({ isOpen, onClose, onEnroll, courseName }: EnrollmentModalProps) {
  const [step, setStep] = useState<'info' | 'credentials'>('info');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    institution: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  if (!isOpen) return null;

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefone é obrigatório';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Senha deve ter no mínimo 6 caracteres';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'As senhas não coincidem';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setStep('credentials');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep2()) return;
    
    setIsSubmitting(true);
    
    try {
      // Enviar dados para o formulário
      const form = new FormData();
      form.append('name', formData.name);
      form.append('email', formData.email);
      form.append('phone', formData.phone);
      form.append('institution', formData.institution);
      form.append('course', courseName);
      
      await fetch('https://readdy.ai/api/form/d5vsn9kkjhtioh5jsc8g', {
        method: 'POST',
        body: new URLSearchParams(form as any)
      });
      
      // Salvar dados do usuário localmente
      const userData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        institution: formData.institution,
        password: formData.password
      };
      
      localStorage.setItem('courseUser', JSON.stringify(userData));
      
      setSubmitSuccess(true);
      
      setTimeout(() => {
        onEnroll(userData);
        onClose();
        setSubmitSuccess(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          institution: '',
          password: '',
          confirmPassword: ''
        });
        setStep('info');
      }, 1500);
      
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      setErrors({ submit: 'Erro ao processar inscrição. Tente novamente.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#CC1B1B] to-[#00CCCC] p-6 rounded-t-3xl relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-all"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
              <i className="ri-user-add-line text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Inscrição no Curso</h2>
              <p className="text-white/90 text-sm mt-1">{courseName}</p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="px-6 pt-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                step === 'info' ? 'bg-[#0099CC] text-white' : 'bg-[#0099CC] text-white'
              }`}>
                {step === 'credentials' ? <i className="ri-check-line"></i> : '1'}
              </div>
              <span className="text-sm font-semibold text-gray-700">Dados Pessoais</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-3">
              <div className={`h-full bg-[#0099CC] transition-all duration-300 ${
                step === 'credentials' ? 'w-full' : 'w-0'
              }`}></div>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                step === 'credentials' ? 'bg-[#0099CC] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                2
              </div>
              <span className="text-sm font-semibold text-gray-700">Criar Conta</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form id="enrollment-form" data-readdy-form onSubmit={handleSubmit} className="px-6 pb-6">
          {submitSuccess ? (
            <div className="py-12 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-check-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Inscrição Realizada!</h3>
              <p className="text-gray-600">Bem-vindo ao curso. Redirecionando...</p>
            </div>
          ) : (
            <>
              {step === 'info' ? (
                <div className="space-y-4">
                  {/* Nome */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nome Completo <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                        <i className="ri-user-line text-lg"></i>
                      </div>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Digite seu nome completo"
                        className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0099CC]/20 transition-all ${
                          errors.name ? 'border-red-500' : 'border-gray-200'
                        }`}
                      />
                    </div>
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {errors.name}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Este nome aparecerá no seu certificado</p>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="seu@email.com"
                        className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0099CC]/20 transition-all ${
                          errors.email ? 'border-red-500' : 'border-gray-200'
                        }`}
                      />
                    </div>
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Telefone */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefone <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(00) 00000-0000"
                        className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0099CC]/20 transition-all ${
                          errors.phone ? 'border-red-500' : 'border-gray-200'
                        }`}
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  {/* Escola / Instituição */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Escola / Instituição
                      <span className="ml-2 text-xs font-normal text-gray-400">(opcional)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                        <i className="ri-building-4-line text-lg"></i>
                      </div>
                      <input
                        type="text"
                        name="institution"
                        value={formData.institution}
                        onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                        placeholder="Ex: Escola Municipal João da Silva"
                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0099CC]/20 transition-all"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Nos ajuda a entender melhor o nosso público</p>
                  </div>

                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="w-full bg-gradient-to-r from-[#CC1B1B] to-[#00CCCC] text-white font-bold py-3 rounded-xl hover:shadow-lg transition-all whitespace-nowrap"
                  >
                    Continuar
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Senha */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Criar Senha <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                        <i className="ri-lock-line text-lg"></i>
                      </div>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Mínimo 6 caracteres"
                        className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0099CC]/20 transition-all ${
                          errors.password ? 'border-red-500' : 'border-gray-200'
                        }`}
                      />
                    </div>
                    {errors.password && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {errors.password}
                      </p>
                    )}
                  </div>

                  {/* Confirmar Senha */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Confirmar Senha <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                        <i className="ri-lock-line text-lg"></i>
                      </div>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        placeholder="Digite a senha novamente"
                        className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0099CC]/20 transition-all ${
                          errors.confirmPassword ? 'border-red-500' : 'border-gray-200'
                        }`}
                      />
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {errors.confirmPassword}
                      </p>
                    )}
                  </div>

                  {errors.submit && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-700 text-sm">
                      <i className="ri-error-warning-line"></i>
                      {errors.submit}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setStep('info')}
                      className="flex-1 bg-gray-100 text-gray-700 font-bold py-3 rounded-xl hover:bg-gray-200 transition-all whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line mr-2"></i>
                      Voltar
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-gradient-to-r from-[#CC1B1B] to-[#00CCCC] text-white font-bold py-3 rounded-xl hover:shadow-lg transition-all disabled:opacity-50 whitespace-nowrap"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Processando...
                        </>
                      ) : (
                        <>
                          Finalizar Inscrição
                          <i className="ri-check-line ml-2"></i>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </form>

        {/* Footer Info */}
        {!submitSuccess && (
          <div className="px-6 pb-6 pt-2">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center text-blue-600">
                <i className="ri-information-line text-lg"></i>
              </div>
              <div className="flex-1">
                <p className="text-xs text-blue-900 font-semibold mb-1">Curso 100% Gratuito</p>
                <p className="text-xs text-blue-700">
                  Ao se inscrever, você terá acesso completo ao curso e poderá acompanhar seu progresso em tempo real.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
